import React, { useEffect, useMemo, useState } from "react";
import Container from "../components/Container";
import GuideDownload from "../components/GuideDownload";
import { getApiBase } from "../lib/api";
import useDownloadCount from "../hooks/useDownloadCount";
import { nf0 } from "../utils/downloadsCounter";


function getCanonicalKey(g, slugParam) {
  return String(g?.slug || g?.id || g?.guid || slugParam || "").trim();
}
function validISO(d) {
  if (!d) return null;
  const t = Date.parse(d);
  if (!Number.isFinite(t)) return null;
  if (t > Date.now()) return null; // avoid “future” start dates
  return new Date(t).toISOString();
}


// Pull slug from hash routes or query string (no react-router-dom needed)
function getSlugFromUrl() {
  try {
    const hash = window.location.hash || "";
    // matches #/guides/<slug> or #/guide/<slug> or #/preview/<slug>
    const m = hash.match(/#\/(?:guides?|preview)\/([^?]+)/i);
    if (m && m[1]) return decodeURIComponent(m[1]);
    const url = new URL(window.location.href);
    return url.searchParams.get("slug") || url.searchParams.get("id") || "";
  } catch {
    return "";
  }
}


// Replace your DownloadCount with this
function DownloadCount({ guide, slugParam }) {
  if (!guide) return null;
  const key = getCanonicalKey(guide, slugParam);

  // base_downloads can be "1,234" or number
  const base = Number(String(guide.base_downloads ?? 0).replace(/,/g, "")) || 0;

  // Only pass a valid ISO start date if it parses & isn’t future
  const startDateISO =
    validISO(guide.downloads_start) ||
    validISO(guide.date) ||
    undefined;

  const { total } = useDownloadCount({ slug: key, base, startDateISO });

  // Show an ellipsis while loading; if the hook ever returns 0, show 0
  const label =
    total == null ? "…" : `${nf0.format(total)} downloads`;

  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-[#003366]">
      {label}
    </span>
  );
}


export default function GuidePreview(props) {
  const slugParam = props?.slug ?? getSlugFromUrl();
  const [guide, setGuide] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");
  const [pageCount, setPageCount] = useState(null);
  const [err, setErr] = useState(null);
  const [posterReady, setPosterReady] = useState(true);
// show <img src={guide.image} /> while pdf first page promise.pending
// after first page render: setPosterReady(false)


  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = await getApiBase();
        const res = await fetch(`${base}/api/guides/list`);
        if (!res.ok) throw new Error(await res.text().catch(() => "Failed to load guides"));
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json?.guides || json?.items || []);
        if (!Array.isArray(list) || !list.length) throw new Error("No guides returned");

        const key = String(slugParam || "").toLowerCase();
        const g =
          list.find(x => String(x.slug || "").toLowerCase() === key) ||
          list.find(x => String(x.id || "").toLowerCase() === key) ||
          list[0];

        if (!g) throw new Error("Guide not found");
        if (cancelled) return;

        setGuide(g);

        const idOrSlug = g.id ?? g.slug ?? slugParam;
        const previewUrl =
          `${base}/api/guides/preview?guide=${encodeURIComponent(idOrSlug)}&pages=2`
            .replace(/^http:/, "https:");
        setPreviewPdfUrl(previewUrl);

        try {
          const metaRes = await fetch(`${base}/api/guides/meta?guide=${encodeURIComponent(idOrSlug)}`);
          if (metaRes.ok) {
            const meta = await metaRes.json();
            if (!cancelled && Number.isFinite(meta.pages)) setPageCount(meta.pages);
          }
        } catch {}
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, [slugParam]);

  // Keep the higher page count (preview only has 2)
  useEffect(() => {
    function onMessage(e) {
      const d = e?.data;
      if (d && d.type === "ETHIRA_PDF_META" && Number.isFinite(d.pages)) {
        setPageCount(curr => (curr == null ? d.pages : Math.max(curr, d.pages)));
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const viewerSrcDoc = useMemo(() => {
    if (!previewPdfUrl) return "";
    let absUrl = previewPdfUrl;
    try { absUrl = new URL(previewPdfUrl, window.location.origin).toString(); } catch {}
    const safeUrlJson = JSON.stringify(absUrl).replace(/</g, "\\u003c");

    return `<!doctype html>
<html>
<head>
 <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />

  <meta charset="utf-8"/>
  <title>preview</title>
  <base href="${window.location.origin}/" />
  <link rel="stylesheet" href="/pdfjs/web/pdf_viewer.css"/>
  <style>
    html,body{height:100%;margin:0;background:#fff;font:14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial}
    #viewerContainer{position:absolute;top:0;left:0;right:0;height:100%;overflow:auto;background:#fff}
    .pdfViewer .page{margin:12px auto;box-shadow:0 2px 8px rgba(0,0,0,.06);border-radius:6px}
    #msg{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#334155}
  </style>
</head>
<body>
  <div id="viewerContainer" hidden><div id="viewer" class="pdfViewer"></div></div>
  <div id="msg">Loading preview…</div>
  <script id="__ETH_URL__" type="application/json">${safeUrlJson}</script>
  <script type="module">
  const urlNode = document.getElementById("__ETH_URL__");
  let previewUrl = "";
  try { previewUrl = JSON.parse(urlNode.textContent || '""'); } catch {}
  const msg  = document.getElementById("msg");
  const wrap = document.getElementById("viewerContainer");

  // Build absolute URLs from the PARENT page (not iframe)
  const ORIGIN = ${JSON.stringify(window.location.origin)};
  const PDFJS_LIB_URL     = ORIGIN + "/pdfjs/build/pdf.mjs";
  const PDFJS_VIEWER_URL  = ORIGIN + "/pdfjs/web/pdf_viewer.mjs";
  const PDFJS_WORKER_URL  = ORIGIN + "/pdfjs/build/pdf.worker.min.mjs"; // or .mjs/.js as shipped

  async function ensureReachable(u){
    if(typeof u !== "string" || !u) throw new Error("No preview URL");
    const r = await fetch(u, { method: "HEAD" });
    if(!r.ok) throw new Error("Preview not reachable: " + r.status);
    return true;
  }

  (async () => {
    try {
      // ⬇️ Use absolute URLs for module imports inside srcdoc
      const pdfjsLib    = await import(PDFJS_LIB_URL);
      const pdfjsViewer = await import(PDFJS_VIEWER_URL);

      // ⬇️ And set workerSrc to an absolute URL too
      pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;

      const eventBus       = new pdfjsViewer.EventBus();
      const linkService    = new pdfjsViewer.PDFLinkService({ eventBus });
      const findController = new pdfjsViewer.PDFFindController({ eventBus, linkService });
      const viewer = new pdfjsViewer.PDFViewer({
        container: wrap,
        eventBus,
        linkService,
        findController,
        textLayerMode: 1
      });
      linkService.setViewer(viewer);

      await ensureReachable(previewUrl);

      const task = pdfjsLib.getDocument({
        url: previewUrl,
        cMapUrl: ORIGIN + "/pdfjs/cmaps/",
        cMapPacked: true,
        standardFontDataUrl: ORIGIN + "/pdfjs/standard_fonts/",
      });

      const pdf = await task.promise;
      viewer.setDocument(pdf);
      linkService.setDocument(pdf, null);
      wrap.hidden = false;
      msg.remove();

    function chooseScale() {
     // Narrow screens get full page visible; wide screens fill width
      const isNarrow = matchMedia('(max-width: 640px)').matches;
       return isNarrow ? 'page-fit' : 'page-width';
      }

      function applyScale() {
        try {
        viewer.currentScaleValue = chooseScale();
        } catch {}
       }

     // Ensure scale is applied at the right times
     eventBus.on('pagesinit', applyScale);
     eventBus.on('pagesloaded', applyScale);

     // Re-apply on container resize (layout shifts, orientation change, etc.)
     new ResizeObserver(() => applyScale()).observe(wrap);
 
     // Initial apply (in case events fire before)
     applyScale();
    } catch (e) {
      console.error(e);
      msg.textContent = (e && e.message) ? e.message : "Failed to load preview";
    }
  })();
</script>

</body>
</html>`;
  }, [previewPdfUrl]);

  if (err) {
    return (
      <Container className="py-12">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">{err}</div>
      </Container>
    );
  }

  if (!guide) {
    return (
      <Container className="py-12">
        <div className="animate-pulse h-8 w-64 rounded bg-slate-200 mb-4" />
        <div className="animate-pulse h-[70vh] rounded-xl bg-slate-100" />
      </Container>
    );
  }

  return (
    <main>
      <Container className="py-10 space-y-6">
        <div>
          <a href="#/guides" className="text-sm text-[#003366]">◀ Back to Guides</a>
          <h1 className="mt-3 text-3xl font-bold text-[#003366]">{guide.title}</h1>
          {guide.excerpt && <p className="mt-2 text-[#003366]">{guide.excerpt}</p>}
        </div>

        <div className="relative rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden">
          {viewerSrcDoc ? (
            <iframe
              title="Guide preview"
              srcDoc={viewerSrcDoc}
              sandbox="allow-scripts allow-same-origin"
              className="w-full h-[78vh] bg-white"
            />
          ) : (
            <div className="h-[78vh] bg-slate-100" />
          )}

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-white via-white/95 to-transparent z-10" />
          <div className="absolute inset-x-0 bottom-0 z-20 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
            <div className="pointer-events-none absolute -top-6 left-0 right-0 h-6 bg-gradient-to-t from-white/90 to-transparent" />
            <div className="mx-auto max-w-5xl px-4 py-4 sm:flex sm:items-center sm:justify-between relative">
              <div className="flex items-center gap-3">
                {Number.isFinite(pageCount) && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-[#003366]">
                    Full Guide {pageCount} pages
                  </span>
                )}
              
                <p className="text-[#003366] font-medium">
                  Liking the preview? Unlock the full guide as a PDF.
                </p>
              </div>
              <div className="mt-3 sm:mt-0">
                <GuideDownload
                  guideId={guide?.id || guide?.guid || ""}
                  guideTitle={guide?.title || guide?.heading || "Investment Guide"}
                  slug={guide?.slug || guide?.id || guide?.guid}
                  label="Get the full PDF"
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </main>
  );
}
